import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { DIRECTION, SYSTEM_ACCOUNT_TYPE } from 'components/constants';
import i18n from 'i18n';
import {
  adjustAccountAmountRequest,
  createNewAccountRequest,
  disableAccountRequest,
  downloadAccountOpeningLetterRequest,
  enableAccountRequest,
  exportAccountsList,
  exportSystemAccountsBalancesReport,
  getAccountsRequest,
  getSystemAccountsRequest,
  setAccountPrimaryRequest,
  updateSystemAccountIbanRequest
} from 'services/requestAgent';

class AccountsStore {
  isLoading = false;
  isLoadingAccountsCsv = false;
  isLoadingTransitAccountsBalancesCsv = false;
  isInitialized = false;
  error = null;
  accounts = null;
  systemAccounts = null;
  pagination = {
    sortBy: null,
    page: 0,
    size: 20,
    totalPages: 0,
    totalElements: 0,
    direction: DIRECTION.ASC
  };
  filters = {
    types: [],
    statuses: [],
    currencies: [],
    transferProviders: [],
    searchText: ''
  };

  constructor() {
    makeAutoObservable(this);
  }

  resetAccountsStore = () => {
    this.isInitialized = false;
    this.isLoading = false;
    this.isLoadingAccountsCsv = false;
    this.isLoadingTransitAccountsBalancesCsv = false;
    this.error = null;
    this.accounts = null;
    this.systemAccounts = null;
    this.pagination = {
      sortBy: null,
      page: 0,
      size: 20,
      totalPages: 0,
      totalElements: 0,
      direction: DIRECTION.ASC
    };
    this.filters = {
      types: [],
      statuses: [],
      currencies: [],
      transferProviders: [],
      searchText: ''
    };
  };

  setIsLoading = (status) => {
    this.isLoading = status;
    this.error = null;
  };

  setIsLoadingAccountsCsv = (status) => {
    this.isLoadingAccountsCsv = status;
    this.error = null;
  };

  setIsLoadingTransitAccountsBalancesCsv = (status) => {
    this.isLoadingTransitAccountsBalancesCsv = status;
    this.error = null;
  };

  setAccountsPage = (page) => {
    this.pagination.page = page;
  };

  setAccountsPageSize = (size) => {
    this.pagination.size = size;
  };

  setFilter = (fieldName, value) => {
    this.filters[fieldName] = value;
    this.pagination.page = 0;
  };

  setSortData = (sortData) => {
    this.pagination.sortBy = sortData.sortBy;
    this.pagination.direction = sortData.direction;
  };

  setFiltersFromUrl = (params) => {
    // Copying parameters from the URL to the filters object properties
    this.filters.types = params.types || this.filters.types;
    this.filters.statuses = params.statuses || this.filters.statuses;
    this.filters.currencies = params.currencies || this.filters.currencies;
    this.filters.transferProviders = params.transferProviders || this.filters.transferProviders;
    this.filters.searchText = params.searchText || this.filters.searchText;

    // Converting pagination parameters from the URL
    this.pagination.page = parseInt(params.page) || this.pagination.page;
    this.pagination.size = parseInt(params.size) || this.pagination.size;

    // Converting sorting parameters from the URL
    this.pagination.sortBy = params.sort_column || this.pagination.sortBy;
    this.pagination.direction = params.sort_direction || this.pagination.direction;
  };

  getAccounts = async () => {
    this.setIsLoading(true);
    try {
      const {
        content: accountsList,
        number: page,
        size,
        total_elements: totalElements,
        total_pages: totalPages
      } = await getAccountsRequest(
        this.pagination.sortBy,
        this.pagination.page,
        this.pagination.size,
        this.pagination.direction,
        this.filters.searchText,
        this.filters
      );

      runInAction(() => {
        this.isInitialized = true;
        this.accounts = accountsList;
        this.pagination = {
          ...this.pagination,
          page,
          size,
          totalElements,
          totalPages
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  handleExportAccounts = async () => {
    this.setIsLoadingAccountsCsv(true);

    const toastPromise = toast.promise(
      exportAccountsList(
        this.pagination.sortBy,
        this.pagination.page,
        this.pagination.size,
        this.pagination.direction,
        this.filters
      ),
      {
        pending: i18n.getMessage('accountsStore.handleExportAccounts.toastPromise.pending'),
        success: i18n.getMessage('accountsStore.handleExportAccounts.toastPromise.success')
      }
    );

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoadingAccountsCsv(false);
    }
  };

  getSystemAccounts = async () => {
    this.setIsLoading(true);
    try {
      const systemAccounts = await getSystemAccountsRequest(null, SYSTEM_ACCOUNT_TYPE.TRANSIT);

      let systemAccountsList = [];

      if (systemAccounts) {
        systemAccountsList = Object.values(systemAccounts);
      }

      runInAction(() => {
        this.systemAccounts = systemAccountsList;
        this.isInitialized = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  updateSystemAccountIban = async (systemAccountNumber, iban) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(updateSystemAccountIbanRequest(systemAccountNumber, iban), {
      pending: i18n.getMessage('accountsStore.updateSystemAccountIban.toastPromise.pending'),
      success: i18n.getMessage('accountsStore.updateSystemAccountIban.toastPromise.success')
    });

    try {
      const updatedSystemAccount = await toastPromise;

      const updatedSystemAccountsList = this.systemAccounts?.map((systemAccount) =>
        systemAccount.account_number === systemAccountNumber ? updatedSystemAccount : systemAccount
      );

      runInAction(() => {
        this.systemAccounts = updatedSystemAccountsList;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  handleExportSystemAccounts = async () => {
    this.setIsLoadingTransitAccountsBalancesCsv(true);

    const toastPromise = toast.promise(exportSystemAccountsBalancesReport(SYSTEM_ACCOUNT_TYPE.TRANSIT), {
      pending: i18n.getMessage('accountsStore.handleExportSystemAccounts.toastPromise.pending'),
      success: i18n.getMessage('accountsStore.handleExportSystemAccounts.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoadingTransitAccountsBalancesCsv(false);
    }
  };

  handleDisableAccount = async (accountNumber) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(disableAccountRequest(accountNumber), {
      pending: i18n.getMessage('accountsStore.handleDisableAccount.toastPromise.pending'),
      success: i18n.getMessage('accountsStore.handleDisableAccount.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  handleEnableAccount = async (accountNumber) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(enableAccountRequest(accountNumber), {
      pending: i18n.getMessage('accountsStore.handleEnableAccount.toastPromise.pending'),
      success: i18n.getMessage('accountsStore.handleEnableAccount.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  handleCreateNewAccount = async (customerNumber, transferProvider, data) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(createNewAccountRequest(customerNumber, transferProvider, data), {
      pending: i18n.getMessage('accountsStore.handleCreateNewAccount.toastPromise.pending'),
      success: i18n.getMessage('accountsStore.handleCreateNewAccount.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  setAccountPrimary = async (accountNumber) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(setAccountPrimaryRequest(accountNumber), {
      pending: i18n.getMessage('accountsStore.setAccountPrimary.toastPromise.pending'),
      success: i18n.getMessage('accountsStore.setAccountPrimary.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  adjustAccountAmount = async (data) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(adjustAccountAmountRequest(data), {
      pending: i18n.getMessage('accountsStore.adjustAccountAmount.toastPromise.pending'),
      success: i18n.getMessage('accountsStore.adjustAccountAmount.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  downloadAccountOpeningLetter = async (customerNumber, accountNumber) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(downloadAccountOpeningLetterRequest(customerNumber, accountNumber), {
      pending: i18n.getMessage('accountsStore.downloadAccountOpeningLetter.toastPromise.pending'),
      success: i18n.getMessage('accountsStore.downloadAccountOpeningLetter.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };
}

export default new AccountsStore();
